import * as cx from 'classnames';
import {
    FORMS_DEFAULT_EMAIL_FIELD_FORMAT_ERROR,
    FORMS_DEFAULT_NUMBER_FIELD_FORMAT_ERROR,
    FORMS_DEFAULT_REQUIRED_FIELD_ERROR,
} from 'mk/autogenerated/translations/TextField.f44ce131fac3d5175c0ae3e8f9784f67'
import { simpleEnum } from 'mk/common/utils';
import FieldWrapper from 'mk/forms/FieldWrapper';
import * as React from 'react';

const FIELD_TYPES = simpleEnum([
    'text',
    'email',
    'number',
    'password',
    'tel',
]);

// keep in sync with apps/common/utls.py
// https://html.spec.whatwg.org/multipage/forms.html#valid-e-mail-address
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

interface Validation {
    regex: any;
    error: string;
}

interface OwnProps {
    name: string;
    className?: string;
    errors?: string[];
    maxLength?: number;
    placeholder?: string;
    initial?: string;
    label?: string;
    autofocus?: boolean;
    required?: boolean;
    autocomplete?: string;
    autocapitalize?: string;
    autocorrect?: string;
    autoselect?: boolean;
    type?: 'text' | 'email' | 'number' | 'password' | 'tel';
    validations?: Validation[];

    onChange?(value: string);
}

interface State {
    value: string;
}

export class TextField extends React.Component<OwnProps, State> {
    public static defaultProps = {
        initial: '',
        type: 'text',
    };

    public static TYPES = FIELD_TYPES;

    private input: HTMLInputElement;

    public static isFormField() {
        return true;
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            value: props.initial,
        };
    }

    public validate() {
        // number field has empty value if content is not number which is problem
        // because of empty validation message
        if (this.props.type === FIELD_TYPES.number) {
            const input = this.input;
            if (input.validity.badInput) {
                return [null, [FORMS_DEFAULT_NUMBER_FIELD_FORMAT_ERROR]];
            }
        }

        if (this.props.required && !this.state.value) {
            return [null, [FORMS_DEFAULT_REQUIRED_FIELD_ERROR]];
        }

        if (this.props.type === FIELD_TYPES.email) {
            if (!EMAIL_RE.test(this.state.value)) {
                return [null, [FORMS_DEFAULT_EMAIL_FIELD_FORMAT_ERROR]];
            }
        }

        if (this.props.validations && this.props.validations.length) {
            const errors = [];
            this.props.validations.forEach(({regex, error}) => {
                if (!regex.test(this.state.value)) {
                    errors.push(error);
                }
            });
            if (errors.length) {
                return [null, errors];
            }
        }

        return [this.state.value, null];
    }

    public clear() {
        if (!this.hasValue()) {
            return;
        }

        this.setState({
            value: this.props.initial,
        });

        // Notify parent about change
        if (this.props.onChange) {
            this.props.onChange(this.props.initial);
        }
    }

    public hasValue() {
        return this.state.value;
    }

    public getValue() {
        return this.state.value;
    }

    public setValue(value) {
        this.setState({ value });
    }

    public render() {
        const {name, label, type} = this.props;

        return (
            <FieldWrapper
                className={cx('text-input', this.props.className)}
                label={label}
                errors={this.props.errors}
                name={name}
                htmlFor={name}
            >
                <input
                    ref={(r) => this.input = r}
                    type={type}
                    id={name}
                    value={this.state.value}
                    onChange={this.onChange}
                    className="g-form-text-input"
                    maxLength={this.props.maxLength}
                    placeholder={this.props.placeholder}
                    autoComplete={this.props.autocomplete}
                    autoCapitalize={this.props.autocapitalize}
                    autoCorrect={this.props.autocorrect}
                />
            </FieldWrapper>
        );
    }

    public setFocus() {
        // http://note.io/1Jtcl53
        setTimeout(() => {
            this.input.focus();
            if (this.props.autoselect) {
                this.input.select();
            }
        }, 0);
    }

    public select() {
        this.input.select();
    }

    private onChange = (event) => {
        this.setState({value: event.target.value});
        // Notify parent about change
        if (this.props.onChange) {
            this.props.onChange(event.target.value);
        }
    };

}
